/**
 * Access Control API Adapters
 * 
 * These adapters transform DTOs (backend contracts) into domain models (frontend models).
 * 
 * Enterprise Rule:
 * - One transformation per DTO
 * - No guessing, no fallbacks
 * - Fail loudly if contract is violated
 * - Centralized, reusable, testable
 */

import {
  AccessRuleDTO,
  ExpiryConfigDTO,
  ExpiringUserDTO,
  ExpiringCompanyDTO,
} from './accessControl.dto';

/**
 * Frontend Domain Model: Access Control Rule
 */
export interface AccessControlRule {
  id: number;
  name: string;
  description: string;
  ruleType: 'ip_whitelist' | 'ip_blacklist' | 'time_restriction' | 'geo_restriction' | 'custom';
  ruleValue: string;
  isActive: boolean;
  priority: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Frontend Domain Model: Expiry Config
 */
export interface ExpiryConfig {
  id: number;
  name: string;
  description: string;
  expiryType: 'user_access' | 'company_license' | 'document_retention' | 'custom';
  expiryDays: number;
  warningDays: number;
  companyId: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Frontend Domain Model: Expiring Item
 */
export interface ExpiringItem {
  id: number;
  type: 'user' | 'company';
  name: string;
  expiryDate: string;
  daysRemaining: number;
  status: 'critical' | 'warning' | 'ok';
}

/**
 * Adapter: Transform AccessRuleDTO to AccessControlRule
 * 
 * Enterprise Rule: Strict transformation, no fallbacks
 * If backend contract changes, this will fail loudly
 */
export function mapAccessRule(dto: AccessRuleDTO): AccessControlRule {
  return {
    id: dto.id,
    name: dto.name,
    description: dto.description || '',
    ruleType: dto.rule_type,
    ruleValue: dto.rule_value,
    isActive: dto.is_active,
    priority: dto.priority,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
  };
}

/**
 * Adapter: Transform ExpiryConfigDTO to ExpiryConfig
 * 
 * Enterprise Rule: Strict transformation, no fallbacks
 */
export function mapExpiryConfig(dto: ExpiryConfigDTO): ExpiryConfig {
  return {
    id: dto.id,
    name: dto.name,
    description: dto.description || '',
    expiryType: dto.expiry_type,
    expiryDays: dto.expiry_days,
    warningDays: dto.warning_days,
    companyId: dto.company_id,
    isActive: dto.is_active,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
  };
}

/**
 * Adapter: Transform ExpiringUserDTO to ExpiringItem
 * 
 * Enterprise Rule: Strict transformation, no fallbacks
 */
export function mapExpiringUser(dto: ExpiringUserDTO): ExpiringItem {
  // Calculate expiry date from days
  const expiryDate = new Date(Date.now() + dto.expiry_days * 24 * 60 * 60 * 1000);
  
  // Determine status based on days remaining
  let status: 'critical' | 'warning' | 'ok' = 'ok';
  if (dto.expiry_days <= 7) {
    status = 'critical';
  } else if (dto.expiry_days <= 30) {
    status = 'warning';
  }
  
  return {
    id: dto.id,
    type: 'user',
    name: `User #${dto.id}`, // Backend doesn't provide user name
    expiryDate: expiryDate.toISOString(),
    daysRemaining: dto.expiry_days,
    status,
  };
}

/**
 * Adapter: Transform ExpiringCompanyDTO to ExpiringItem
 * 
 * Enterprise Rule: Strict transformation, no fallbacks
 */
export function mapExpiringCompany(dto: ExpiringCompanyDTO): ExpiringItem {
  // Calculate expiry date from days
  const expiryDate = new Date(Date.now() + dto.expiry_days * 24 * 60 * 60 * 1000);
  
  // Determine status based on days remaining
  let status: 'critical' | 'warning' | 'ok' = 'ok';
  if (dto.expiry_days <= 7) {
    status = 'critical';
  } else if (dto.expiry_days <= 30) {
    status = 'warning';
  }
  
  return {
    id: dto.id,
    type: 'company',
    name: dto.entity_name || `Company #${dto.company_id}`,
    expiryDate: expiryDate.toISOString(),
    daysRemaining: dto.expiry_days,
    status,
  };
}

/**
 * Adapter: Transform array of AccessRuleDTO to AccessControlRule[]
 */
export function mapAccessRules(dtos: AccessRuleDTO[]): AccessControlRule[] {
  return dtos.map(mapAccessRule);
}

/**
 * Adapter: Transform array of ExpiryConfigDTO to ExpiryConfig[]
 */
export function mapExpiryConfigs(dtos: ExpiryConfigDTO[]): ExpiryConfig[] {
  return dtos.map(mapExpiryConfig);
}

/**
 * Adapter: Transform expiring users and companies to ExpiringItem[]
 */
export function mapExpiringItems(
  users: ExpiringUserDTO[],
  companies: ExpiringCompanyDTO[]
): ExpiringItem[] {
  return [
    ...users.map(mapExpiringUser),
    ...companies.map(mapExpiringCompany),
  ];
}
